import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fleetService } from "../services/fleetService";
import { useI18n } from "../providers/I18nProvider";
import { useConfirm } from "../providers/ConfirmProvider";
import NoirAlert from "../components/NoirAlert";
import PaginationControls from "../components/PaginationControls";
import { TabBar, FilterBar, SearchInput, FilterField, EmptyState } from "../components/ListToolbar";
import ReservationCard from "../components/fleet/ReservationCard";
import {
  categorize,
  filterReservations,
  sortReservations,
} from "../components/fleet/reservationUtils";
import "../styles/Fleet.css";

const PAGE_SIZE_KEY = "pageSize.adminReservations";

const AdminReservationsPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const [reservations, setReservations] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const clearNotification = useCallback(() => setNotification({ message: "", type: "" }), []);
  const busyRef = useRef(new Set());

  const [tab, setTab] = useState("pending");
  const [tabTouched, setTabTouched] = useState(false);
  const [search, setSearch] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sortKey, setSortKey] = useState("start_desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const parsed = parseInt(localStorage.getItem(PAGE_SIZE_KEY) || "10", 10);
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });

  const load = useCallback(() => {
    setError("");
    return fleetService
      .allReservations()
      .then((data) => setReservations(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.response?.data?.msg || t("fleet.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // Bekleyen yoksa kullanıcıyı boş sekmede bırakma: ilk yüklemede "Yaklaşan"a geç.
  const counts = useMemo(() => {
    const c = { pending: 0, upcoming: 0, past: 0, closed: 0, all: reservations.length };
    const now = Date.now();
    reservations.forEach((r) => {
      const cat = categorize(r, now);
      c[cat] += 1;
      if (cat === "pending" && new Date(r.ends_at).getTime() >= now) c.upcoming += 1;
    });
    return c;
  }, [reservations]);

  useEffect(() => {
    if (!loading && !tabTouched && counts.pending === 0 && tab === "pending") setTab("upcoming");
  }, [loading, tabTouched, counts.pending, tab]);

  const vehicles = useMemo(() => {
    const map = new Map();
    reservations.forEach((r) => map.set(String(r.vehicle_id), r.vehicle_name));
    return [...map.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]), "tr"));
  }, [reservations]);

  const filtered = useMemo(
    () =>
      sortReservations(
        filterReservations(reservations, { tab, search, vehicleId, from, to }),
        sortKey
      ),
    [reservations, tab, search, vehicleId, from, to, sortKey]
  );

  useEffect(() => {
    setPage(1);
  }, [tab, search, vehicleId, from, to, sortKey, pageSize]);

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
  }, [pageSize]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  const hasActiveFilter = Boolean(search || vehicleId || from || to);
  const resetFilters = () => {
    setSearch("");
    setVehicleId("");
    setFrom("");
    setTo("");
  };

  const run = async (id, fn, doneKey) => {
    if (busyRef.current.has(id)) return; // çift tıklama koruması
    busyRef.current.add(id);
    try {
      await fn(id);
      setNotification({ message: t(doneKey), type: "success" });
      await load();
    } catch (err) {
      setNotification({
        message: err.response?.data?.msg || t("fleet.errors.generic"),
        type: "error",
      });
    } finally {
      busyRef.current.delete(id);
    }
  };

  const approve = (id) => run(id, fleetService.approveReservation, "fleetUi.approveDone");
  const reject = async (id) => {
    if (!(await confirm(t("fleetUi.rejectConfirm")))) return;
    run(id, fleetService.rejectReservation, "fleetUi.rejectDone");
  };
  const cancel = async (id) => {
    if (!(await confirm(t("fleet.cancelConfirm")))) return;
    run(id, fleetService.cancelReservation, "fleetUi.cancelDone");
  };

  const tabs = [
    { key: "pending", label: t("fleetUi.tabs.pending"), count: counts.pending, attention: true },
    { key: "upcoming", label: t("fleetUi.tabs.upcoming"), count: counts.upcoming },
    { key: "past", label: t("fleetUi.tabs.past"), count: counts.past },
    { key: "closed", label: t("fleetUi.tabs.closed"), count: counts.closed },
    { key: "all", label: t("fleetUi.tabs.all"), count: counts.all },
  ];

  return (
    <div className="fleet-page">
      <NoirAlert message={notification.message} type={notification.type} onClose={clearNotification} />
      <div className="fleet-page-header">
        <div>
          <h2>{t("fleet.adminReservationsTitle")}</h2>
          <div className="subtitle">{t("fleet.adminReservationsSubtitle")}</div>
        </div>
      </div>

      {error && (
        <div className="fleet-error" role="alert">
          {error}{" "}
          <button type="button" className="fleet-btn secondary" onClick={load}>
            {t("fleetUi.retry")}
          </button>
        </div>
      )}
      {loading ? (
        <div className="fleet-empty">{t("common.loading")}</div>
      ) : (
        <>
          <TabBar
            tabs={tabs}
            value={tab}
            onChange={(k) => {
              setTabTouched(true);
              setTab(k);
            }}
          />
          <FilterBar showReset={hasActiveFilter} onReset={resetFilters}>
            <SearchInput value={search} onChange={setSearch} placeholder={t("fleetUi.filters.search")} />
            <select
              className="leaves-filter-input lt-input"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              aria-label={t("fleetUi.filters.vehicle")}
            >
              <option value="">{t("fleetUi.filters.allVehicles")}</option>
              {vehicles.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <FilterField label={t("fleetUi.filters.from")}>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </FilterField>
            <FilterField label={t("fleetUi.filters.to")}>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </FilterField>
            <select
              className="leaves-filter-input lt-input"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label={t("fleetUi.filters.sort")}
            >
              <option value="start_desc">{t("fleetUi.filters.sortStartDesc")}</option>
              <option value="start_asc">{t("fleetUi.filters.sortStartAsc")}</option>
            </select>
          </FilterBar>

          {filtered.length === 0 ? (
            <EmptyState
              message={reservations.length === 0 ? t("fleet.noReservations") : t("fleetUi.emptyFiltered")}
            />
          ) : (
            <>
              <PaginationControls
                totalItems={filtered.length}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
              <div className="reservation-list">
                {paged.map((r) => {
                  const isFuture = new Date(r.starts_at) > new Date();
                  const canCancel = isFuture && (r.status === "approved" || r.status === "pending");
                  return (
                    <ReservationCard
                      key={r.id}
                      r={r}
                      actions={
                        (r.status === "pending" || canCancel) && (
                          <div className="actions">
                            {r.status === "pending" && (
                              <>
                                <button className="fleet-btn success" type="button" onClick={() => approve(r.id)}>
                                  {t("fleet.approve")}
                                </button>
                                <button className="fleet-btn danger" type="button" onClick={() => reject(r.id)}>
                                  {t("fleet.reject")}
                                </button>
                              </>
                            )}
                            {canCancel && (
                              <button className="fleet-btn secondary" type="button" onClick={() => cancel(r.id)}>
                                {t("fleet.cancel")}
                              </button>
                            )}
                          </div>
                        )
                      }
                    />
                  );
                })}
              </div>
              <PaginationControls
                totalItems={filtered.length}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AdminReservationsPage;
